import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { CircleHelp } from 'lucide-react';
import { cn } from './cn.js';

const controlClass =
  'min-h-11 w-full rounded-sm border border-border bg-bg px-3.5 py-2.5 text-base text-ink outline-none transition placeholder:text-ink-3 hover:border-border-hi focus:border-accent focus:ring-3 focus:ring-accent-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-err aria-[invalid=true]:ring-3 aria-[invalid=true]:ring-[color-mix(in_oklch,var(--err)_24%,transparent)]';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type = 'text', ...props }, ref) => (
    <input ref={ref} type={type} className={cn(controlClass, className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 5, ...props }, ref) => (
  <textarea ref={ref} rows={rows} className={cn(controlClass, 'resize-y', className)} {...props} />
));
Textarea.displayName = 'Textarea';

export function Field({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div className={cn('grid gap-2', className)} {...props} />;
}

export const FieldLabel = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        'font-mono text-xs font-medium uppercase tracking-[0.1em] text-ink-3',
        className,
      )}
      {...props}
    />
  ),
);
FieldLabel.displayName = 'FieldLabel';

export function FieldDescription({ className, ...props }: React.ComponentPropsWithoutRef<'p'>) {
  return <p className={cn('m-0 text-sm leading-5 text-ink-3', className)} {...props} />;
}

export function FieldError({ className, ...props }: React.ComponentPropsWithoutRef<'p'>) {
  return <p role="alert" className={cn('m-0 text-sm leading-5 text-err', className)} {...props} />;
}

export function InfoTooltip({ children, label }: { children: ReactNode; label: string }) {
  const id = useId();
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        className="-m-3 inline-grid size-11 place-items-center rounded-full text-ink-3 outline-none transition hover:text-ink focus-visible:text-accent focus-visible:ring-2 focus-visible:ring-accent-ring"
      >
        <CircleHelp aria-hidden="true" className="size-3.5" />
      </button>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none invisible fixed inset-x-4 top-20 z-50 w-auto rounded-sm border border-border-hi bg-surface px-3 py-2 text-left text-sm font-normal leading-5 text-ink-2 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 lg:absolute lg:inset-x-auto lg:top-auto lg:right-0 lg:bottom-[calc(100%+0.5rem)] lg:w-64"
      >
        {children}
      </span>
    </span>
  );
}

export function FieldShell({
  children,
  className,
  description,
  error,
  htmlFor,
  label,
}: {
  children: ReactElement<{
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  }>;
  className?: string;
  description?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  label: ReactNode;
}) {
  const generatedId = useId();
  const fieldId = htmlFor ?? children.props.id ?? generatedId;
  const descriptionId = description ? `${fieldId}-description` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [children.props['aria-describedby'], descriptionId, errorId]
    .filter(Boolean)
    .join(' ');
  const controlProps: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  } = { id: fieldId };
  if (describedBy) controlProps['aria-describedby'] = describedBy;
  if (error) controlProps['aria-invalid'] = true;
  else if (children.props['aria-invalid'] !== undefined) {
    controlProps['aria-invalid'] = children.props['aria-invalid'];
  }
  const control = isValidElement(children) ? cloneElement(children, controlProps) : children;
  return (
    <Field className={className}>
      <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
      {control}
      {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
      {error && <FieldError id={errorId}>{error}</FieldError>}
    </Field>
  );
}
